import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { Form, Link } from '@remix-run/react'
import { z } from 'zod'
import { Spacer } from '~/components/ui/spacer.tsx'
import { StatusButton } from '~/components/ui/inputs/button/status-button.tsx'
import {
  lastNameSchema,
  firstNameSchema,
  passwordSchema,
  emailSchema,
  phoneSchema,
} from '~/utils/user-validation.ts'
import {
  CheckboxField,
  ErrorList,
  Field,
} from '~/components/ui/inputs/forms.tsx'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { checkboxSchema } from '~/utils/zod-extensions.ts'
import { type CompanyType } from 'types/enums.ts'

export const SignupFormSchema = z
  .object({
    email: emailSchema,
    confirmEmail: emailSchema.optional(),
    firstName: firstNameSchema,
    billingEmail: z.string().optional(),
    lastName: lastNameSchema,
    company: z.string().min(2),
    phone: phoneSchema,
    password: passwordSchema,
    confirmPassword: passwordSchema,
    agreeDNA: checkboxSchema('Obligatorio'),
    redirectTo: z.string().optional(),
  })
  .superRefine(
    async ({ email, confirmEmail, password, confirmPassword }, ctx) => {
      if (confirmEmail && confirmEmail !== email) {
        ctx.addIssue({
          path: ['confirmEmail'],
          code: 'custom',
          message: 'Los emails no coinciden',
        })
      }

      if (confirmPassword !== password) {
        ctx.addIssue({
          path: ['confirmPassword'],
          code: 'custom',
          message: 'Las contraseñas no coinciden',
        })
      }
    },
  )

interface SignupFormValues {
  email: string
  confirmEmail?: string
  firstName: string
  lastName: string
  company: string
  billingEmail?: string
  phone: string
  password: string
  confirmPassword: string
  agreeDNA: boolean
  redirectTo?: string
}

interface SignupFormProps {
  actionData: any
  isPending: boolean
  redirectTo: string
  defaultValues?: Partial<SignupFormValues>
  readonly?: {
    email?: boolean
    company?: boolean
    billingEmail?: boolean
  }
  onValidate?: (formData: FormData) => ReturnType<typeof parseWithZod>
  companyType?: CompanyType
}

export function SignupForm({
  actionData,
  isPending,
  redirectTo,
  defaultValues = {},
  readonly = {},
  onValidate,
  companyType,
}: SignupFormProps) {
  const [form, fields] = useForm<SignupFormValues>({
    id: 'signup-form',
    constraint: getZodConstraint(SignupFormSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupFormSchema })
      return result
    },
    shouldRevalidate: 'onBlur',
    defaultValue: {
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      phone: '',
      password: '',
      confirmPassword: '',
      agreeDNA: false,
      redirectTo: '',
      billingEmail: defaultValues.billingEmail,
      ...defaultValues,
    },
  })

  const showConfirmEmail = !defaultValues.email

  const getFieldClassName = (isReadOnly?: boolean) =>
    `
    [&>input]:rounded-full 
    [&>input]:border-2 
    [&>input]:border-dashed 
    [&>input]:border-primary 
    [&>input]:bg-white 
    [&>input]:text-center
    ${
      isReadOnly
        ? '[&>input]:bg-gray-50 [&>input]:cursor-not-allowed [&>input]:opacity-60'
        : ''
    }
  `.trim()

  return (
    <>
      <h1 className="text-h4">Registro de Empresa</h1>
      <p className="text-body-md text-foreground-muted">
        Por favor introduzca los detalles.
      </p>
      <Spacer size="4xs" />
      <Form
        method="POST"
        className="flex flex-col gap-4"
        {...getFormProps(form)}
      >
        <AuthenticityTokenInput />
        <HoneypotInputs />
        <input name="companyType" type="hidden" value={companyType} />
        <div className="flex gap-2">
          <Field
            labelProps={{ htmlFor: fields.firstName.id, children: 'Nombre' }}
            inputProps={{
              ...getInputProps(fields.firstName, { type: 'text' }),
              autoComplete: 'given-name',
              placeholder: 'Nombre',
            }}
            className={getFieldClassName()}
            errors={fields.firstName.errors}
          />
          <Field
            labelProps={{ htmlFor: fields.lastName.id, children: 'Apellidos' }}
            inputProps={{
              ...getInputProps(fields.lastName, { type: 'text' }),
              autoComplete: 'family-name',
              placeholder: 'Apellidos',
            }}
            className={getFieldClassName()}
            errors={fields.lastName.errors}
          />
        </div>
        <Field
          labelProps={{
            htmlFor: fields.company.id,
            children: 'Nombre empresa',
          }}
          inputProps={{
            ...getInputProps(fields.company, { type: 'text' }),
            autoComplete: 'empresa',
            placeholder: 'Empresa',
            readOnly: readonly.company,
          }}
          className={getFieldClassName(readonly.company)}
          errors={fields.company.errors}
        />
        <Field
          labelProps={{
            htmlFor: fields.phone.id,
            children: 'Teléfono',
          }}
          inputProps={{
            ...getInputProps(fields.phone, { type: 'text' }),
            autoComplete: 'phone',
            placeholder: '6XX XXX XXX',
          }}
          className={getFieldClassName()}
          errors={fields.phone.errors}
        />
        <Field
          labelProps={{ htmlFor: fields.email.id, children: 'Email' }}
          inputProps={{
            ...getInputProps(fields.email, { type: 'email' }),
            autoComplete: 'email',
            placeholder: 'Email',
            readOnly: readonly.email,
          }}
          className={getFieldClassName(readonly.email)}
          errors={fields.email.errors}
        />
        {showConfirmEmail && (
          <Field
            labelProps={{
              htmlFor: fields.confirmEmail?.id,
              children: 'Confirmar email',
            }}
            inputProps={{
              ...getInputProps(fields.confirmEmail, { type: 'email' }),
              onPaste: event => {
                event.preventDefault()
              },
              autoComplete: 'off',
              placeholder: 'Confirmar email',
              readOnly: readonly.email,
            }}
            className={getFieldClassName(readonly.email)}
            errors={fields.confirmEmail?.errors}
          />
        )}
        <Field
          labelProps={{
            htmlFor: fields.billingEmail.id,
            children: 'Email de facturación',
          }}
          inputProps={{
            ...getInputProps(fields.billingEmail, { type: 'email' }),
            autoComplete: 'email',
            placeholder: 'Email de facturación',
            readOnly: readonly.billingEmail,
          }}
          className={getFieldClassName(readonly.billingEmail)}
          errors={fields.billingEmail.errors}
        />
        <Field
          labelProps={{ htmlFor: fields.password.id, children: 'Contraseña' }}
          inputProps={{
            ...getInputProps(fields.password, { type: 'password' }),
            autoComplete: 'new-password',
            placeholder: 'Contraseña',
          }}
          className={getFieldClassName()}
          errors={fields.password.errors}
        />
        <Field
          labelProps={{
            htmlFor: fields.confirmPassword.id,
            children: 'Confirmar contraseña',
          }}
          inputProps={{
            ...getInputProps(fields.confirmPassword, { type: 'password' }),
            onPaste: event => {
              event.preventDefault()
            },
            autoComplete: 'new-password',
            placeholder: 'Confirmar contraseña',
          }}
          className={getFieldClassName()}
          errors={fields.confirmPassword.errors}
        />
        <CheckboxField
          labelProps={{
            htmlFor: fields.agreeDNA.id,
            children: (
              <div>
                *Acepto las condiciones de
                <Link
                  to={`/confidentiality`}
                  className="font-semibold text-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  {' confidencialidad'}
                </Link>
              </div>
            ),
          }}
          checkboxProps={getInputProps(fields.agreeDNA, { type: 'checkbox' })}
          errors={fields.agreeDNA.errors}
        />
        <input name={fields.redirectTo.name} type="hidden" value={redirectTo} />
        <ErrorList errors={form.errors} id={form.errorId} />
        <div className="flex items-center justify-between gap-6">
          <StatusButton
            className="w-full rounded-full"
            status={isPending ? 'pending' : (form.status ?? 'idle')}
            type="submit"
            disabled={isPending}
          >
            Crear cuenta
          </StatusButton>
        </div>
      </Form>
    </>
  )
}
